// src/components/institution/sections/AboutInstitutionSection.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Award, Users, Target, Heart, ArrowRight, CheckCircle, Star, TrendingUp } from 'lucide-react';
import { FlipBookCard } from './FlipBook';

export const AboutInstitutionSection = ({ data, settings, styling, onLearnMore }) => {
  const [hoveredValue, setHoveredValue] = useState(null);

  // Helpers
  const parseValues = (values) => {
    if (!values) return [];
    if (Array.isArray(values)) return values.map(v => `${v}`.trim()).filter(Boolean);
    if (typeof values === 'string') {
      return values
        .split(/[,\|;]+|\s{2,}/) // commas, pipes, semicolons, or 2+ spaces
        .map(v => v.trim())
        .filter(Boolean);
    }
    return [];
  };

  const prefersReducedMotion = usePrefersReducedMotion();

  const institutionValues = parseValues(settings?.values);
  const primary = styling?.primaryColor || '#6366f1';
  const secondary = styling?.secondaryColor || '#8b5cf6';

  const stats = useMemo(() => ([
    { number: NumberOnly(settings?.studentCount) ?? 2500, suffix: '+', label: 'Students', icon: Users, color: '#10b981' },
    { number: NumberOnly(settings?.staffCount) ?? 150, suffix: '+', label: 'Staff', icon: Award, color: '#f59e0b' },
    { number: NumberOnly(settings?.programCount) ?? 45, suffix: '+', label: 'Programs', icon: TrendingUp, color: '#ef4444' },
    { number: NumberOnly(settings?.establishedYear) ?? 1985, suffix: '', label: 'Established', icon: Star, color: '#8b5cf6' }
  ]), [settings]);

  return (
    <section className="relative py-20 px-4 overflow-hidden">
      {/* Soft Textured Background */}
      <DotGridBackground primary={primary} secondary={secondary} />

      <div className="relative max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: primary }} />
            <span className="text-sm font-medium text-gray-700">About Our Institution</span>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
          {/* Left: Mission / Vision / Values */}
          <div className="lg:col-span-7 space-y-8">
            {/* Replace the two mission/vision cards block */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FlipBookCard
                primary={primary}
                secondary={secondary}
                mission={settings?.mission}
                vision={settings?.vision}
                cycleMs={6500}
              />
              {/* Values */}
            {institutionValues.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                       style={{ backgroundColor: hexWithAlpha('#ef4444', 0.12) }}>
                    <Heart className="h-5 w-5 text-red-500" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900">Our Values</h3>
                </div>

                <ul className="grid grid-cols-2 sm:grid-cols-2 gap-3">
                  {institutionValues.map((value, i) => (
                    <li key={`${value}-${i}`}>
                      <button
                        type="button"
                        onMouseEnter={() => setHoveredValue(i)}
                        onMouseLeave={() => setHoveredValue(null)}
                        className="group w-full"
                        aria-label={`Value: ${value}`}
                      >
                        <div
                          className="relative px-4 py-3 rounded-xl text-sm font-medium text-center border transition-transform duration-200 will-change-transform"
                          style={{
                            background: hoveredValue === i
                              ? `linear-gradient(0deg, ${hexWithAlpha(primary, 0.12)}, ${hexWithAlpha(primary, 0.12)})`
                              : '#fff',
                            borderColor: hexWithAlpha(primary, 0.35),
                            color: hoveredValue === i ? primary : '#111827',
                          }}
                        >
                          <span className="absolute inset-0 rounded-xl pointer-events-none"
                                style={{ boxShadow: hoveredValue === i ? `0 10px 24px ${hexWithAlpha(primary, 0.2)}` : 'none' }} />
                          <CheckCircle className={`h-4 w-4 mx-auto mb-1 transition-transform ${hoveredValue === i ? 'scale-110' : ''}`} />
                          {value}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </div>
          </div>

          {/* Right: Image or Stats */}
          <div className="lg:col-span-5">
            <div className="relative h-full">
              {data?.aboutImage ? (
                <figure className="group relative h-full rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={data.aboutImage}
                    alt={`About ${data?.botName || 'institution'}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/30 to-transparent text-white/90 text-sm">
                    {/* optional image caption slot */}
                  </figcaption>
                </figure>
              ) : (
                <div className="grid grid-cols-2 gap-4 h-full">
                  {stats.map((s, idx) => {
                    const Icon = s.icon;
                    return (
                      <div key={`${s.label}-${idx}`} className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 shadow-sm">
                        <div className="text-center">
                          <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
                               style={{ backgroundColor: hexWithAlpha(s.color, 0.12) }}>
                            <Icon className="h-6 w-6" style={{ color: s.color }} />
                          </div>
                          <div className="text-3xl font-extrabold" style={{ color: s.color }}>
                            <CountUp to={s.number} duration={1200} disabled={prefersReducedMotion} />
                            {s.suffix}
                          </div>
                          <div className="mt-1 text-gray-600 font-medium">{s.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex items-center gap-4 bg-white/80 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-gray-200 shadow-sm">
            <div className="text-left">
              <h4 className="text-base md:text-lg font-semibold text-gray-900">Ready to join our community?</h4>
              <p className="text-gray-600 text-sm">Discover what makes us different.</p>
            </div>
            <button
              onClick={onLearnMore}
              className="group inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold border transition-all duration-200 hover:-translate-y-0.5"
              style={{ borderColor: hexWithAlpha(primary, 0.4), color: primary }}
            >
              Learn more
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        {/* Badges */}
        {(settings?.institutionType || settings?.accreditation) && (
          <div className="flex justify-center mt-8">
            <div className="flex flex-wrap gap-3 justify-center">
            
              {settings?.accreditation && (
                <Badge label="Accredited" value={settings.accreditation} color={primary} />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------- Small, focused building blocks ---------- */

function CardWithRail({ railColor, children }) {
  return (
    <div className="relative bg-white/85 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm">
      <span className="absolute left-0 top-0 h-full w-[6px] rounded-l-2xl" style={{ background: railColor }} aria-hidden />
      {children}
    </div>
  );
}

function Badge({ label, value, color }) {
  return (
    <div className="bg-white/90 backdrop-blur-sm px-5 py-2 rounded-full border border-gray-200 shadow-sm">
      <span className="text-gray-600 text-sm">{label}: </span>
      <span className="font-semibold" style={{ color }}>{value}</span>
    </div>
  );
}

/**
 * CountUp: minimal, accessible; disables animation if prefers-reduced-motion.
 */
function CountUp({ to, duration = 1000, disabled = false }) {
  const [val, setVal] = useState(0);
  const elRef = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (disabled) {
      setVal(to);
      return;
    }
    const el = elRef.current;
    if (!el) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          animate(0, to, duration, setVal);
          io.disconnect();
        }
      });
    }, { threshold: 0.3 });

    io.observe(el);
    return () => io.disconnect();
  }, [to, duration, disabled]);

  return <span ref={elRef} aria-label={`${to}`} role="text">{formatNumber(val)}</span>;
}

function animate(from, to, duration, onUpdate) {
  const start = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic

  const tick = (now) => {
    const p = Math.min(1, (now - start) / duration);
    const v = Math.round(from + (to - from) * ease(p));
    onUpdate(v);
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(!!mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

function NumberOnly(x) {
  if (x == null) return null;
  const n = typeof x === 'string' ? parseInt(x.replace(/[^\d]/g, ''), 10) : Number(x);
  return Number.isFinite(n) ? n : null;
}

function formatNumber(n) {
  return new Intl.NumberFormat().format(Math.max(0, n));
}

function hexWithAlpha(hex, a = 0.12) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * Background: soft dots + radial lights; keeps it classy.
 */
function DotGridBackground({ primary, secondary }) {
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(1200px 600px at 120% -10%, ${hexWithAlpha(primary, .15)} 0%, transparent 60%),
             radial-gradient(1000px 500px at -10% 110%, ${hexWithAlpha(secondary, .12)} 0%, transparent 60%),
             linear-gradient(180deg, #f9fafb 0%, #ffffff 60%, #f5f7ff 100%)`
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            `radial-gradient(circle at 1px 1px, ${hexWithAlpha('#000', .06)} 1px, transparent 0)`,
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at center, black 60%, transparent 100%)'
        }}
        aria-hidden
      />
    </>
  );
}

function FlipPage({ side = 'front', tint, accent, title, icon, text }) {
  return (
    <article
      className="absolute inset-0 rounded-xl border border-gray-200/60 shadow-sm"
      style={{
        background: '#ffffff',
        backfaceVisibility: 'hidden',
        transform: side === 'back' ? 'rotateY(180deg)' : 'rotateY(0deg)',
      }}
    >
      {/* subtle page tint */}
      <div
        className="absolute inset-0 rounded-xl"
        style={{ background: `linear-gradient(180deg, ${tint}, transparent 60%)` }}
        aria-hidden
      />
      <div className="relative p-2 sm:p-3 md:p-4 lg:p-5 xl:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
            style={{ background: tint }}
          >
            {icon}
          </div>
          <h3 className="text-lg md:text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <p className="text-gray-600 leading-relaxed">{text}</p>

        {/* page curl hint */}
        <span
          className="absolute bottom-0 right-0 w-10 h-10 rounded-tl-xl"
          style={{
            background:
              'radial-gradient(22px 22px at 100% 100%, rgba(0,0,0,.08) 0, rgba(0,0,0,0) 70%)',
          }}
          aria-hidden
        />
        {/* thin edge highlight */}
        <span
          className="absolute top-0 right-0 h-full w-[2px] opacity-60"
          style={{ background: `linear-gradient(180deg, ${hexWithAlpha(accent, 0.6)}, transparent)` }}
          aria-hidden
        />
      </div>
    </article>
  );
}